import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import { AppEntityViewKey } from "@/types/common.types.ts";
import { AI_ASSISTANT_PANEL_WIDTH } from "@/components/ai-assistant/ai-assistant-panel-width.ts";
import { SIDEBAR_PANEL_WIDTH } from "@skailer-com/uikit";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import Cookies from "js-cookie";

export interface AuthStore {
  token: string | null;
  userId: number | null;
  setToken: (newToken: string | null) => void;
  setUserId(newUserId: number | null): void;
}

interface CookieOptions {
  path?: string;
  secure?: boolean;
  expires?: number;
  domain?: string;
}

const cookieOptions: CookieOptions = {
  path: "/",
  secure: true,
  expires: 30,
  domain: "." + import.meta.env.VITE_DOMAIN,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      token: null,
      userId: null,
      setUserId: (newUserId: number | null) => {
        set(() => ({ userId: newUserId }));
      },
      setToken: (newToken: string | null) => {
        set(() => ({ token: newToken }));
        if (newToken) {
          Cookies.set("token", newToken, cookieOptions);
        } else {
          Cookies.remove("token", { path: "/", domain: "." + import.meta.env.VITE_DOMAIN });
        }
      },
    }),
    {
      name: "auth",
    }
  )
);

export interface EmployeeAppConfigStore {
  recruitRightPaneSize: number;
  recruitLeftPaneSize: number;
  sidebarIsCollapsed: boolean;
  /** px */
  sidebarPanelWidth: number;
  aiAssistantIsOpen: boolean;
  aiAssistantWasOpened: boolean;
  /** px */
  aiAssistantPanelWidth: number;
  recruitSidebarJobSorting: ApiSorting;
  setRecruitRightPaneSize: (size: number) => void;
  setRecruitLeftPaneSize: (size: number) => void;
  setSidebarIsCollapsed: (isCollapsed: boolean) => void;
  setSidebarPanelWidth: (width: number) => void;
  setAiAssistantIsOpen: (isOpen: boolean) => void;
  setAiAssistantPanelWidth: (width: number) => void;
  setRecruitSidebarJobSorting: (sorting: ApiSorting) => void;

  entityViews: Partial<Record<AppEntityViewKey, number>>;
  setEntityViewed: (key: AppEntityViewKey) => void;
  isEntityViewed: (key: AppEntityViewKey) => boolean;
  getEntityViewTime: (key: AppEntityViewKey) => number | undefined;
}

export const useEmployeeAppConfigStore = create<EmployeeAppConfigStore>()(
  persist(
    (set, get) => ({
      recruitRightPaneSize: 250,
      recruitLeftPaneSize: 250,
      sidebarIsCollapsed: false,
      sidebarPanelWidth: SIDEBAR_PANEL_WIDTH.initial,
      aiAssistantIsOpen: false,
      aiAssistantWasOpened: false,
      aiAssistantPanelWidth: AI_ASSISTANT_PANEL_WIDTH.initial,
      recruitSidebarJobSorting: {
        fields: [{ field: "touchpoint", direction: ApiSortingDirection.desc }],
      },
      entityViews: {},

      setRecruitRightPaneSize: (size) => {
        set(() => ({ recruitRightPaneSize: size }));
      },
      setRecruitLeftPaneSize: (size) => {
        set(() => ({ recruitLeftPaneSize: size }));
      },
      setSidebarIsCollapsed: (isCollapsed) => {
        set(() => ({ sidebarIsCollapsed: isCollapsed }));
      },
      setSidebarPanelWidth: (width) => {
        set(() => ({ sidebarPanelWidth: width }));
      },
      setAiAssistantIsOpen: (isOpen) => {
        set((state) => ({
          aiAssistantIsOpen: isOpen,
          aiAssistantWasOpened: state.aiAssistantWasOpened || isOpen,
        }));
      },
      setAiAssistantPanelWidth: (width) => {
        set(() => ({ aiAssistantPanelWidth: width }));
      },
      setRecruitSidebarJobSorting: (sorting) => {
        set(() => ({ recruitSidebarJobSorting: sorting }));
      },

      setEntityViewed: (key) => {
        const now = Date.now();
        set((state) => ({
          entityViews: { ...state.entityViews, [key]: now },
        }));
      },

      isEntityViewed: (key) => {
        return get().entityViews[key] !== undefined;
      },

      getEntityViewTime: (key) => {
        return get().entityViews[key];
      },
    }),
    {
      name: "employeeAppConfig",
    }
  )
);
